/* The homepage's motion — one gesture, one screen, and each screen holds its ground while the
   next rises over it. The client, comparing the homepage to a house's: "let it flow seamlessly
   when sliding like miu miu, now it feels like a powerpoint" — and the reference's landing page
   answers the wheel with exactly this: the screen you are on stops moving, the screen you asked
   for travels over it, one layer in motion per gesture. Every movement here is one glide — taken
   the moment the gesture arrives, done in 500ms on the reference's own curve (the EASE below).

   The hold is the platform's own scroll. Every screen (a .v2-phase, through its .shopify-section
   wrapper) is measured and, when its last full frame is on the glass, pinned — at its own top
   edge, or bottom-aligned when the screen is taller than the window so nothing it holds is ever
   cut (the hero at 1440 is; the journal used to be, until its band was brought inside one
   screen). A screen whose container ends before the one
   that must cover it — the SEO band, the last card inside main — cannot be
   held by a pin (sticky never escapes its containing block), so its closing frame is held by a
   measured transform instead (the deck block below). None of it exists without JavaScript: the
   classes doing the pinning are added by this script.

   The hero is a single still frame ("the hero should have only 1 image, not 5"): nothing on this
   page plays on its own, and the wheel and the swipe belong to the page from its first pixel.

   The wheel decides every move the moment the gesture arrives — a slow trackpad push would
   otherwise move the page a little before the glide began, the lurch the client read as a
   slideshow — and a flick's momentum, arriving under the move's own lock, is swallowed with it
   (what continues after the lock is deliberate — see the FLOOR at the wheel below).
   Below 900 the finger is the input and the platform's scroller is the engine: there is no deck
   at all — no pins, no held transforms (they moved under the finger whenever the phone's URL bar
   changed the viewport mid-scroll; the client, 2026-10-01: "the past section feels glitchy and
   jumpy in the background"), and nothing here claims a touch.
   A pinch-zoom (ctrl or ⌘ with the wheel) and the menu sheet are the two things left to the
   platform. Under prefers-reduced-motion the glide is instant; the screens and the gestures
   behave as ever. */
(() => {
  const hero = document.querySelector('[data-hero]');
  if (!hero) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ── the hero's film ──────────────────────────────────────────────────────────────────────────
     The client, 2026-10-13: "Replace hero for this video, make sure its optimized". The video is a
     store File (Content ▸ Files, one encode per box shape — see design/gen-hero-video.mjs) and it
     sits ON the poster frame, which is the still the page paints first: the film fades in only
     once it is actually playing, so a slow connection, a blocked autoplay or a visitor who asked
     for reduced motion keeps a finished photograph rather than a black rectangle.

     It plays while the hero is on screen and pauses when the deck has covered it — a background
     film that keeps decoding under the journal is battery the visitor pays for and never sees. */
  const film = hero.querySelector('[data-hero-film]');
  if (film) {
    const show = () => film.classList.add('v2-hero__film--on');
    const play = () => { const p = film.play(); if (p && p.catch) p.catch(() => {}); };
    film.addEventListener('playing', show, { once: true });
    if (!reduced.matches) {
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? play() : film.pause()));
        }, { threshold: 0.1 }).observe(film);
      } else {
        play();
      }
    }
  }

  /* ── the glide ────────────────────────────────────────────────────────────────────────────────
     Every movement of this page is one glide: taken the moment the gesture is made, done in the
     reference's own 500ms, then still. `glide` counts the runs, so anything newer — another
     gesture — cancels the one in flight by leaving it an id it no longer matches. `lockUntil` is
     the swallow: while a glide runs, and for a moment after it, incoming gestures are the tail of
     the one that moved us. */
  const wide = window.matchMedia('(min-width: 900px)');
  const GLIDE = 500; // ms for a whole screen — the reference's own duration
  const COOLDOWN = 740; // the swallow: a move owns this long, glide and momentum tail together
  const STEP = 40; // px of travel that counts as a deliberate notch
  const FLOOR = 14; // px an event must carry to count at all — a decaying tail tick cannot add up
  let glide = 0;
  let lockUntil = 0;
  let travel = 0;

  /* CSS `ease` — cubic-bezier(0.25, 0.1, 0.25, 1) — the reference's own curve, run in script
     because this page's motion is window scroll rather than a transformed layer. Twenty
     bisections put the answer within a millionth of a pixel; the old ease-out cubic answered the
     gesture harder and settled later than the reference does. */
  const EASE = (() => {
    const [x1, y1, x2, y2] = [0.25, 0.1, 0.25, 1];
    const cx = (t) => 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t;
    const cy = (t) => 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t;
    return (x) => {
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 20; i++) {
        const mid = (lo + hi) / 2;
        if (cx(mid) < x) lo = mid;
        else hi = mid;
      }
      return cy((lo + hi) / 2);
    };
  })();

  const glideTo = (top) => {
    const from = window.scrollY;
    const distance = Math.round(top - from);
    if (!distance) return;
    const id = ++glide;
    if (reduced.matches) { window.scrollTo(0, top); return; }
    // A screen takes GLIDE; a longer travel keeps its proportion inside the same ceiling, so
    // nothing is either abrupt or leaden.
    const ms = Math.min(GLIDE + 120, Math.max(300, (Math.abs(distance) / window.innerHeight) * GLIDE));
    const start = performance.now();
    lockUntil = Math.max(lockUntil, Date.now() + ms + 140);
    const frame = (now) => {
      if (id !== glide) return;
      const t = Math.min(1, (now - start) / ms);
      window.scrollTo(0, Math.round(from + distance * EASE(t)));
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  /* ── the deck ────────────────────────────────────────────────────────────────────────────────
     The screens of the homepage as a stack of cards. Each wrapper is given the pin (the shell
     carries the sticky rule under .v2-deck) and its own top offset: `min(0, window - height)`.
     For a screen the height of the window that is 0 — it freezes at its top edge, exactly the
     view it was resting on. For a taller one it is negative — the screen runs on until its last
     full frame reaches the fold (so everything it holds stays reachable), and freezes there,
     which is also the moment the screen below begins to enter; the two align by construction, so
     the incoming card always meets a still page and never a moving one. */
  const root = document.documentElement;
  const phases = [...document.querySelectorAll('.v2-phase')];
  const tops = new Map(); // each screen's layout top, measured with the pins off
  const holds = []; // screens a pin cannot carry — their containers end before the card that
                    // must cover them does — held by a measured transform instead

  /* Measured with the pins OFF: sticky changes where a screen PAINTS, and both offsetTop and a
     rect would read the pinned position — so the root class comes off for the pass, every screen
     reports its layout position, and the class goes back on before the browser paints. Nothing
     visible happens in between: no frame is drawn while this runs.

     Each screen is then either pinned or held. The pin (the shell's sticky) holds a screen still
     only while its containing block still reaches past the card that must cover it — for the
     hero, the object, the play, the house and the newsletter it does (the newsletter against
     the footer is the equality case, and it holds to the last pixel). For a screen whose
     container ends too soon — the SEO band, the last card inside main — the
     hold does the job instead: the distance the scroll would have carried the screen is given
     back as a transform. */
  const measure = () => {
    root.classList.remove('v2-deck');
    holds.forEach((hold) => { hold.el.style.transform = ''; });
    holds.length = 0;
    /* ⚠️ The deck is a DESKTOP gesture. Below 900 the platform's scroller owns the page, and a
       phone's own viewport is not stable — the URL bar collapses and expands mid-scroll, every
       such resize re-ran this pass, and the pin offsets moved under the finger: the section
       being covered read as "glitchy and jumpy in the background" (the client, 2026-10-01).
       The phone gets a plain document: no pins, no measured holds, no deck class — and the
       measured tops are cleared so bounds() falls back to live rects there. Crossing the
       boundary either way re-measures through the resize listener below. */
    if (!wide.matches) {
      tops.clear();
      return;
    }
    const list = phases.map((el) => {
      const rect = el.getBoundingClientRect();
      return { el, wrap: el.closest('.shopify-section'), top: Math.round(rect.top + window.scrollY), h: Math.round(rect.height) };
    });
    const docH = Math.round(document.documentElement.scrollHeight);
    list.forEach((card) => tops.set(card.el, card.top));
    holds.length = 0;
    for (const card of list) {
      const parent = card.wrap?.parentElement;
      if (!card.wrap || !parent) continue;
      const next = card.wrap.nextElementSibling;
      const parentBottom = Math.round(parent.getBoundingClientRect().bottom + window.scrollY);
      const nextTop = next ? Math.round(next.getBoundingClientRect().top + window.scrollY) : docH;
      if (parentBottom >= nextTop + card.h) {
        card.wrap.classList.add('v2-pin');
        card.wrap.style.setProperty('--v2-pin', `${Math.min(0, window.innerHeight - card.h)}px`);
      } else {
        card.wrap.classList.remove('v2-pin');
        holds.push({ el: card.el, from: card.top + card.h - window.innerHeight, cap: docH - (card.top + card.h), at: -1 });
      }
    }
    root.classList.add('v2-deck');
  };

  /* The holds. From the moment a held screen's closing frame lands, the distance the scroll would
     have carried it is given back as a transform, capped at the room the closing cards vacate —
     the cap keeps a hold from becoming scrollable overflow (uncapped, the document grew by the
     shift and the end of the page moved away as the visitor approached it). Always measured from
     the landing, so scrolling back releases each exactly as it was caught. */
  const holdCards = () => {
    for (const hold of holds) {
      const shift = Math.min(hold.cap, Math.max(0, Math.round(window.scrollY - hold.from)));
      if (shift === hold.at) continue;
      hold.at = shift;
      hold.el.style.transform = shift ? `translateY(${shift}px)` : '';
    }
  };
  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        holdCards();
      });
    },
    { passive: true },
  );

  /* ── the homepage's phases ────────────────────────────────────────────────────────────────────
     The homepage is read a screen at a time. The client, on the play band: "make this full screen
     ... each scroll should move customers to next phase of the page" — so every band marked
     .v2-phase fills the window at 900 and up (the shell does that part), and the scroll only ever
     comes to rest on a phase's top edge. One gesture of any size past the threshold moves exactly
     one phase, in the direction it was going; a nudge smaller than the threshold returns to the
     phase it came from.

     The wheel below decides every one of those moves itself, the moment the gesture is made; this
     list is what it moves to, and what the settle further down falls back on for the gestures the
     wheel never sees — a trackpad's momentum, a dragged scrollbar, Page Up and Page Down. */
  const bounds = () => {
    const list = [];
    for (const el of phases) {
      // The measured layout top, never a live rect: a pinned screen would report where it PAINTS.
      list.push(tops.get(el) ?? Math.round(el.getBoundingClientRect().top + window.scrollY));
    }
    // A held screen taller than the window rests a second time where its hold freezes — its
    // closing frame, the screen's own second view. Without it a gentle notch inside the screen
    // read as a full-screen jump (the "weird scroll down" reported on entering Vacanza Club).
    for (const hold of holds) {
      if (hold.from - (tops.get(hold.el) ?? 0) >= minTravel() * 2) list.push(hold.from);
    }
    list.sort((a, b) => a - b);
    const rests = [];
    for (const top of list) {
      // Two rests closer than a deliberate gesture cannot be told apart; keep the first.
      if (rests.length && top - rests[rests.length - 1] < minTravel()) continue;
      rests.push(top);
    }
    const end = Math.max(0, Math.round(document.documentElement.scrollHeight - window.innerHeight));
    if (rests.length && end - rests[rests.length - 1] >= minTravel()) rests.push(end); // the end rests too
    return rests;
  };
  // A wheel notch is ~100px and a trackpad flick is far larger, so the threshold is a deliberate
  // 5% of the window (min 40px): a soft two-finger push still counts as a gesture.
  const minTravel = () => Math.max(40, Math.round(window.innerHeight * 0.05));
  const nearest = (list, y) => list.reduce((best, v) => (Math.abs(v - y) < Math.abs(best - y) ? v : best), list[0]);
  let rest = 0; // the phase the last move left the page on
  let hop = null; // the bound OUR OWN move is travelling to, so landing there is adoption, not a rule

  /* One whole screen in the direction the gesture went. False when there is nothing that way — the
     top of the first phase, the end of the page — and the caller then leaves the gesture alone. */
  const movePage = (dir) => {
    const list = bounds();
    const i = list.indexOf(nearest(list, window.scrollY));
    const target = i === -1 ? undefined : list[i + dir];
    if (target === undefined) return false;
    rest = target;
    hop = target;
    glideTo(target);
    return true;
  };

  /* The wheel. One notch or flick moves the page a whole screen — the hero's photograph is the
     first screen now, and nothing is played frame by frame. Nothing is left to the browser to
     scroll first: the gesture is swallowed from its very first event (a slow trackpad push would
     otherwise move the page a little before the glide began — the lurch the client read as a
     slideshow), and a flick's momentum is swallowed with it — the WHOLE burst, until a pause
     separates the next gesture. The homepage shows no scrollbar at all (v2-nobar, in the shell's
     reset) — the swallow stands in for the bar the other pages keep.

     Two things are left to the platform: a pinch-zoom (ctrl or ⌘ with the wheel), and the menu
     sheet, which scrolls itself when it is taller than the window — the overlays' panels the
     same, and the page behind them is frozen while one is open. */
  window.addEventListener(
    'wheel',
    (event) => {
      if (event.ctrlKey || event.metaKey) return;
      if (event.target.closest?.('.v2-menu__panel')) return;
      /* While an overlay is open the page is frozen: the dialog's panel scrolls itself, and a
         wheel anywhere else does nothing at all — the screen behind must not move. */
      if (root.classList.contains('v2-overlay-open')) {
        if (!event.target.closest?.('.v2-modal__panel')) event.preventDefault();
        return;
      }
      event.preventDefault();
      const now = Date.now();
      /* One MOVE owns the moment, not one gesture. The lock is the swallow: nothing counts
         while a glide travels. After it lifts, the same hand may move the page again — the
         client, 2026-09-30: "scrolling feels super slow, i have to do it multiple times", after
         the burst model this replaces made one physical gesture the ceiling, so a continuous
         push or a spinning wheel worked once and then went dead until a pause. What keeps the
         OLD fault — a flick's momentum tail firing a second move at the Vacanza Club rest
         (157px of leftover tail, the "slight weird scroll down") — from returning is the
         FLOOR: by the time the lock expires that tail's mass arrives as ticks of ~8px and
         less, and a tick under the floor accumulates nothing. A deliberate notch (a wheel
         click, a firm push) clears it in one event, and a hand that keeps pushing keeps
         clearing it — one screen per move, for as long as the hand keeps asking. */
      if (now < lockUntil) return;
      const delta =
        event.deltaMode === 1
          ? event.deltaY * 16
          : event.deltaMode === 2
            ? event.deltaY * window.innerHeight
            : event.deltaY;
      if (Math.abs(delta) < FLOOR) return;
      if ((delta > 0) !== (travel > 0)) travel = 0; // a new direction starts its own count
      travel += delta;
      if (Math.abs(travel) < STEP) return;
      const dir = travel < 0 ? -1 : 1;
      travel = 0;
      if (movePage(dir)) {
        lockUntil = Math.max(lockUntil, now + COOLDOWN);
      }
    },
    { passive: false },
  );


  /* ── the rests the wheel never sees ───────────────────────────────────────────────────────────
     A trackpad's momentum, a dragged scrollbar, the keyboard, a restored position: they arrive as
     plain scroll events. On a wide screen the page still only comes to rest on a phase's top edge,
     so the same rule runs once the scroll has settled — from where the last move left it (`rest`)
     and the direction it has travelled since. Below 900 the phone keeps the browser's own
     scroller, so nothing here second-guesses a finger. */
  let settle = 0;
  const onSettle = () => {
    const y = window.scrollY;
    const list = bounds();
    const landed = hop !== null && Math.abs(hop - y) <= 8; // our own move, done
    const stayed = Math.abs(rest - y) <= 8; // back where the gesture started
    if (landed || stayed) { rest = y; hop = null; return; }
    const delta = y - rest;
    const down = delta > 0;
    const beside = down
      ? list.find((v) => v > rest + 8)
      : [...list].reverse().find((v) => v < rest - 8);
    if (beside === undefined) { rest = y; hop = null; return; }
    const span = Math.abs(beside - rest);
    // A gesture counts when it covers half the way to the phase ahead, or the usual threshold —
    // whichever is smaller, so a short last gap is never a place the page cannot reach.
    const reach = Math.min(minTravel(), Math.max(8, span / 2));
    if (Math.abs(delta) < reach) { if (delta) { hop = rest; glideTo(rest); } return; } // a nudge
    // One gesture moves ONE phase — a trackpad flick's momentum overshoots and is pulled back. A
    // travel beyond twice that (a dragged scrollbar, a restored position) is a jump, not a
    // gesture, and is honoured where it lands.
    const target = Math.abs(delta) > span * 2 ? nearest(list, y) : beside;
    rest = target;
    hop = target;
    glideTo(target);
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!wide.matches) return;
      clearTimeout(settle);
      settle = setTimeout(onSettle, 150);
    },
    { passive: true },
  );
  // A reload restores wherever the visitor was; put the page back on a phase.
  if (wide.matches) {
    setTimeout(() => {
      const y = window.scrollY;
      const list = bounds();
      let best = list[0] ?? 0;
      for (const v of list) if (Math.abs(v - y) < Math.abs(best - y)) best = v;
      rest = best;
      if (Math.abs(best - y) > 8) { hop = best; glideTo(best); }
    }, 500);
  }

  measure();
  holdCards();
  window.addEventListener('resize', () => { measure(); holdCards(); }, { passive: true });
  // The webfonts settle after this script runs and label metrics move the screens a little;
  // re-measure once they have, so the pins and the rests are the numbers actually on screen.
  if (document.fonts?.ready) document.fonts.ready.then(() => { measure(); holdCards(); });
  // And any later layout shift — a band growing by a line, an image settling — re-measures too:
  // the pins and the rests are only as good as the last clean pass, and this keeps it current.
  // The pass writes only classes and custom properties, and a transform cannot resize its own
  // border box, so the observer cannot be woken by its own work.
  if ('ResizeObserver' in window) {
    new ResizeObserver(() => {
      measure();
      holdCards();
    }).observe(document.body);
  }
})();
